import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiText, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useGetAIPlanStubQuery, useAddAIPlanStubCampaignBuilderMutation, useEditAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'

import { CampaignForm, AddCampaignSectionStep, CampaignFormFields } from '../../../../components/CampaignForm'

export enum EditAIPlanCampaignFlyoutMode {
  Add = 'add',
  Edit = 'edit'
}

export interface EditAIPlanCampaignFlyoutProps {
  editMode: EditAIPlanCampaignFlyoutMode
  selectedCampaignBuilderId?: string
  offAdCritterName?: string
  offAdCritterColor?: string
  onClose: () => void
  onBack?: () => void
}

interface AIPlanPageParams {
  aiPlanId: string
}

const EditAIPlanCampaignFlyout: React.FC<EditAIPlanCampaignFlyoutProps> = ({ editMode, selectedCampaignBuilderId, offAdCritterName, offAdCritterColor, onClose, onBack }) => {
  const { aiPlanId } = useParams<AIPlanPageParams>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })

  const isOffAdCritterAdd = editMode === EditAIPlanCampaignFlyoutMode.Add && !!offAdCritterName
  const [newCampaignStep, setNewCampaignStep] = useState<AddCampaignSectionStep>(isOffAdCritterAdd ? AddCampaignSectionStep.CampaignDetails : AddCampaignSectionStep.CampaignType)

  const didFireUpdate = useRef(false)

  const [campaignBuilder, setCampaignBuilder] = useState<CampaignFormFields | undefined>(() => {
    if (editMode === EditAIPlanCampaignFlyoutMode.Add) {
      if (offAdCritterName) {
        return {
          name: 'New Campaign',
          shortSummary: '',
          longSummary: '',
          audienceSummary: '',
          geoSummary: '',
          seasonalitySummary: '',
          campaignType: MasterCampaignPlanCampaignBuilderType.OffAdCritter,
          isOffAdCritter: true,
          offAdCritterName,
          offAdCritterColor: offAdCritterColor || '#999999',
          retargetSite: false,
          startDate: moment().add(3, 'days'),
          intervalUnit: IntervalUnit.Month,
          intervalFrequency: 1,
          intervalDuration: 1,
          segmentTargets: [],
          zips: []
        }
      }
      return {
        name: 'New Campaign',
        shortSummary: '',
        longSummary: '',
        audienceSummary: '',
        geoSummary: '',
        seasonalitySummary: '',
        campaignType: MasterCampaignPlanCampaignBuilderType.TV,
        retargetSite: false,
        startDate: moment().add(3, 'days'),
        intervalUnit: IntervalUnit.Month,
        intervalFrequency: 1,
        intervalDuration: 1,
        segmentTargets: [],
        zips: []
      }
    }

    const builder = stub?.campaignBuilders.find(b => b.id === selectedCampaignBuilderId)
    if (!builder) {
      return undefined
    }
    return {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary: builder.audienceSummary,
      geoSummary: builder.geoSummary,
      seasonalitySummary: builder.seasonalitySummary,
      campaignType: builder.campaignType as unknown as MasterCampaignPlanCampaignBuilderType,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      cpm: builder.cpm,
      marginOfError: builder.marginOfError ? builder.marginOfError * 100 : undefined,
      includeInProjections: builder.includeInProjections,
      includeInGeoHeatmap: builder.includeInGeoHeatmap,
      keywords: builder.keywords || [],
      keywordDescription: builder.keywordDescription,
      cpc: builder.cpc,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      startDate: builder.startDate!,
      intervalUnit: builder.intervalUnit || IntervalUnit.Month,
      intervalFrequency: builder.intervalFrequency || 1,
      intervalDuration: builder.intervalDuration || 1,
      segmentTargets: builder.segmentTargets || [],
      zips: builder.zips || []
    }
  })

  const [addCampaignBuilder, { isSuccess: isAddSuccess, isLoading: isAdding }] = useAddAIPlanStubCampaignBuilderMutation()
  const [editCampaignBuilder, { isSuccess: isEditSuccess, isLoading: isEditing }] = useEditAIPlanStubCampaignBuilderMutation()

  const isSuccess = isAddSuccess || isEditSuccess

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess, onClose])

  if (!campaignBuilder) {
    return null
  }

  const buildRequest = (fields: CampaignFormFields) => {
    const existingBuilder = editMode === EditAIPlanCampaignFlyoutMode.Edit
      ? stub?.campaignBuilders.find(b => b.id === selectedCampaignBuilderId)
      : undefined

    return {
      name: fields.name,
      shortSummary: fields.shortSummary,
      longSummary: fields.longSummary,
      audienceSummary: fields.audienceSummary,
      audienceDetails: existingBuilder?.audienceDetails || '',
      geoSummary: fields.geoSummary,
      geoDetails: existingBuilder?.geoDetails || '',
      seasonalitySummary: fields.seasonalitySummary,
      seasonalityDetails: existingBuilder?.seasonalityDetails || '',
      campaignType: fields.campaignType as unknown as string,
      isOffAdCritter: fields.isOffAdCritter || false,
      offAdCritterName: fields.offAdCritterName,
      offAdCritterColor: fields.offAdCritterColor,
      intervalBudget: fields.intervalBudget!,
      retargetSite: fields.retargetSite,
      isTotalBudget: existingBuilder?.isTotalBudget || false,
      startDate: moment(fields.startDate).toISOString(),
      endDate: existingBuilder?.endDate ? moment(existingBuilder.endDate).toISOString() : undefined,
      intervalUnit: fields.intervalUnit as unknown as string,
      intervalFrequency: fields.intervalFrequency,
      intervalDuration: fields.intervalDuration,
      segmentTargets: fields.segmentTargets,
      audienceSegmentLooksUp: existingBuilder?.audienceSegmentLooksUp || [],
      targetingType: existingBuilder?.targetingType as unknown as string | undefined,
      regions: existingBuilder?.regions || [],
      dmas: existingBuilder?.dmas || [],
      cities: existingBuilder?.cities || [],
      zips: fields.zips,
      cpm: fields.cpm,
      marginOfError: fields.marginOfError ? fields.marginOfError / 100 : undefined,
      includeInProjections: fields.includeInProjections || false,
      includeInGeoHeatmap: fields.includeInGeoHeatmap || false,
      keywords: fields.keywords,
      keywordDescription: fields.keywordDescription,
      cpc: fields.cpc
    }
  }

  const onSave = () => {
    if (!stub || !campaignBuilder.intervalBudget) {
      return
    }

    const request = buildRequest(campaignBuilder)

    if (editMode === EditAIPlanCampaignFlyoutMode.Add) {
      addCampaignBuilder({
        aiPlanStubId: stub.id,
        request
      })
    } else {
      if (!selectedCampaignBuilderId) return
      editCampaignBuilder({
        aiPlanStubId: stub.id,
        campaignBuilderId: selectedCampaignBuilderId,
        aiPlanId,
        request
      })
    }

    didFireUpdate.current = true
  }

  const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color) || color.startsWith('linear-gradient') || color.startsWith('radial-gradient')
  }

  const isSaveDisabled = () => {
    if (!campaignBuilder) return true
    if (campaignBuilder.isOffAdCritter && !campaignBuilder.offAdCritterName) return true
    if (campaignBuilder.isOffAdCritter && (!campaignBuilder.offAdCritterColor || !isValidHexColor(campaignBuilder.offAdCritterColor))) return true

    const budgetValidation = !campaignBuilder.isOffAdCritter ? (!!campaignBuilder.intervalBudget && campaignBuilder.intervalBudget < 100) || !campaignBuilder.intervalBudget : !campaignBuilder.intervalBudget
    const isGoogle = campaignBuilder.isOffAdCritter && campaignBuilder.offAdCritterName === 'Google'
    const hasProjectionData = !!campaignBuilder.cpm || !!campaignBuilder.cpc || !!campaignBuilder.marginOfError
    const cpmInvalid = !isGoogle && (!campaignBuilder.cpm || campaignBuilder.cpm <= 0)
    const cpcInvalid = isGoogle && (!campaignBuilder.cpc || campaignBuilder.cpc <= 0)
    const projectionValidation = campaignBuilder.isOffAdCritter && hasProjectionData && ((cpmInvalid && !isGoogle) || (cpcInvalid && isGoogle) || !campaignBuilder.marginOfError || campaignBuilder.marginOfError <= 0)

    return !campaignBuilder.name || moment(campaignBuilder.startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3 || campaignBuilder.intervalDuration <= 0 || budgetValidation || projectionValidation
  }

  const getCampaignTypeLabel = () => {
    if (campaignBuilder.isOffAdCritter && campaignBuilder.offAdCritterName) {
      return campaignBuilder.offAdCritterName
    }
    if (campaignBuilder.campaignType === MasterCampaignPlanCampaignBuilderType.Audio) {
      return 'Streaming Audio'
    }
    return campaignBuilder.campaignType
  }

  return (
    <AcFlyout onClose={onClose}>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>{editMode === EditAIPlanCampaignFlyoutMode.Add ? 'Add Campaign' : `Edit ${getCampaignTypeLabel()} Campaign`}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <CampaignForm
          campaignBuilder={campaignBuilder}
          setCampaignBuilder={setCampaignBuilder}
          step={editMode === EditAIPlanCampaignFlyoutMode.Add ? newCampaignStep : AddCampaignSectionStep.CampaignDetails}
          offAdCritterCustomOptions={[]}
        />
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        {editMode === EditAIPlanCampaignFlyoutMode.Add ? (
          <EuiFlexGroup direction='row' alignItems='center'>
            {newCampaignStep === AddCampaignSectionStep.CampaignType && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={onClose}>Cancel</EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={() => setNewCampaignStep(AddCampaignSectionStep.CampaignDetails)}>
                    Next
                  </EuiButton>
                </EuiFlexItem>
              </>
            )}
            {newCampaignStep === AddCampaignSectionStep.CampaignDetails && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={isOffAdCritterAdd && onBack ? onBack : () => setNewCampaignStep(AddCampaignSectionStep.CampaignType)}>Back</EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={onSave} isLoading={isAdding} isDisabled={isSaveDisabled()}>
                    Save
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size='xs'>
                    <p>* Required fields</p>
                  </EuiText>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        ) : (
          <EuiFlexGroup direction='row' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={onClose}>Cancel</EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton fill onClick={onSave} isLoading={isEditing} isDisabled={isSaveDisabled()}>
                Save
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size='xs'>
                <p>* Required fields</p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditAIPlanCampaignFlyout
